import { CheckIcon } from "@heroicons/react/24/outline";
import { CommandItem } from "@/core/components/ui/command";
import { Stack } from "@/core/components/ui/stack";
import type { ApPurchaseOrderOption } from "@/accounts-payable/types";
import {
  purchaseOrderLabel,
  purchaseOrderOptionMeta,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

/**
 * One ServiceTitan PO in the manual PO picker: its number and the tags that
 * matter for the choice, then status, job, vendor, total and date, then the
 * references that tie it to a vendor document.
 */
export const PurchaseOrderOptionItem = ({
  option,
  selected,
  disabled,
  onSelect,
}: {
  option: ApPurchaseOrderOption;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) => {
  const references = [
    option.vendor_document_number &&
      `Vendor invoice ${option.vendor_document_number}`,
    option.memo,
  ].filter(Boolean);

  return (
    <CommandItem
      value={String(option.st_purchase_order_id)}
      // ServiceTitan voided it; the server would refuse it.
      disabled={disabled || option.canceled}
      onSelect={onSelect}
      className={cn("items-start gap-3 px-3 py-2", {
        "bg-bg-secondary": selected,
      })}
    >
      <CheckIcon
        className={cn("mt-1 h-4 w-4 flex-none", { "opacity-0": !selected })}
      />
      <Stack gap="none" className="min-w-0 flex-1">
        <Stack direction="row" gap="sm" align="baseline" className="flex-wrap">
          <span className="font-semibold">{purchaseOrderLabel(option)}</span>
          {option.current && (
            <span className="text-sm text-tertiary-text">Current PO</span>
          )}
          {option.same_vendor && !option.current && (
            <span className="text-sm text-tertiary-text">Same vendor</span>
          )}
          {/* Picking it moves the document, so say so before the click. */}
          {option.moves_job && (
            <span className="text-sm text-gold-60">Moves to another job</span>
          )}
        </Stack>
        <span className="text-sm text-secondary-text">
          {purchaseOrderOptionMeta(option).join(" · ")}
        </span>
        {references.length > 0 && (
          <span className="truncate text-sm text-secondary-text">
            {references.join(" · ")}
          </span>
        )}
      </Stack>
    </CommandItem>
  );
};
